import {
  IsArray,
  IsBoolean,
  IsEmail,
  IsIn,
  IsInt,
  IsLatitude,
  IsLongitude,
  IsObject,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  MaxLength,
  Min,
  ValidateIf,
} from 'class-validator';
import { BYLAW_CATEGORIES } from './bylaw-workflows.dto';
import { OPTIONAL_PAN_PATTERN, type ParcelDetail, type ParcelInputDto } from './parcels.dto';
import type { CreateComplaintDto } from './complaints.dto';
import type { IntakeConfirmation, IntakeConfirmationInput } from './intake-checks';

/** SNB PAN "Owner Type" mapped to the tombstone dropdown (Stage 1 spec §6). */
export type OwnerType = 'individual' | 'corporation' | 'organization' | 'government' | 'unknown';
export const OWNER_TYPES: OwnerType[] = [
  'individual',
  'corporation',
  'organization',
  'government',
  'unknown',
];

/**
 * The 15 counties of New Brunswick, for the tombstone county picker.
 *
 * Values are the canonical English name exactly as the SNB PAN record prints
 * it, deliberately *not* a slug: `gov_file.county` is free-text `varchar(120)`
 * already holding transcribed PAN values, so storing the printed name keeps
 * every existing row valid and needs no migration.
 *
 * For the same reason the DTOs keep `county` as an unvalidated string rather
 * than gaining an `@IsIn` — a legacy or hand-typed value must not start failing
 * validation just because the intake form became a dropdown.
 *
 * County (not Regional Service Commission) is the right unit here: it is what
 * the PAN record carries, and NB's land registry offices are county-based.
 */
export const NB_COUNTIES: readonly string[] = [
  'Albert',
  'Carleton',
  'Charlotte',
  'Gloucester',
  'Kent',
  'Kings',
  'Madawaska',
  'Northumberland',
  'Queens',
  'Restigouche',
  'Saint John',
  'Sunbury',
  'Victoria',
  'Westmorland',
  'York',
];

/**
 * Stored county value → i18n key suffix under `gov.county.*`. Only Saint John
 * actually differs in French (Saint-Jean), but every county goes through the
 * table so the FR side is a translation task rather than a special case.
 *
 * Callers should fall back to the raw stored value on a miss: files predating
 * the picker, or PAN records printing something unexpected, must still display.
 */
export const NB_COUNTY_KEY: Readonly<Record<string, string>> = {
  Albert: 'albert',
  Carleton: 'carleton',
  Charlotte: 'charlotte',
  Gloucester: 'gloucester',
  Kent: 'kent',
  Kings: 'kings',
  Madawaska: 'madawaska',
  Northumberland: 'northumberland',
  Queens: 'queens',
  Restigouche: 'restigouche',
  'Saint John': 'saintJohn',
  Sunbury: 'sunbury',
  Victoria: 'victoria',
  Westmorland: 'westmorland',
  York: 'york',
};

/** Where the owner/property identity came from — auto-set by upload-and-extract. */
export type OwnerSource = 'snb_land_registry' | 'manual' | 'other';

/**
 * Subject-property category, captured at Stage 1 §D.
 *
 * This is not a descriptive label — it decides whether the Entry & Access
 * Module renders at Stage 2. Per the v46 flowchart it *replaced* the former
 * stream-branch decision, so it is the only thing standing between an officer
 * and an entry pathway they may not need.
 *
 * A plain `string` because the list is administrable — see `taxonomy.dto.ts`.
 * It was a literal union with the entry behaviour baked into two membership
 * arrays here; that behaviour now lives on each row in `gov_property_type`,
 * where an administrator adding a value has to state it. Resolve it with
 * {@link requiresEntryAuthority}, or read the server-resolved
 * `FileDetail.entryAccessApplies`.
 */
export type PropertyType = string;

/**
 * The shared Stages 1–4 lifecycle plus a terminal `closed` stage. Per the v47
 * docs every escalation type flows through the same first four stages; the
 * D&U-specific Steps 5–9 live behind the escalation selector and are tracked
 * separately (see escalation contracts), so the file-level stage stops at
 * `escalation`.
 */
export type FileStage =
  | 'intake' // Stage 1 — complaint intake & file creation
  | 'review' // Stage 2 — officer review & pre-visit planning
  | 'site_visit' // Stage 3 — site visit & evidence gathering
  | 'classification' // Stage 4 — completion, classification & compliance
  | 'escalation' // handed to the escalation type selector
  | 'closed';

export const FILE_STAGES: FileStage[] = [
  'intake',
  'review',
  'site_visit',
  'classification',
  'escalation',
  'closed',
];

/** Workflow-wide status that is orthogonal to the stage (a file can be at the
 * classification stage but `paused` while a warrant sub-flow resolves). */
export type FileStatus =
  | 'active'
  | 'awaiting_external' // in a clerk/council/court queue
  | 'paused' // main workflow paused during a warrant sub-flow
  | 'emergency' // s.144(9) emergency flag raised
  | 'closed';

/**
 * Stage 1 §F priority level. The three values are the spec's, verbatim.
 *
 * `routine` was called `normal` until the §F fields landed; the rename is a
 * data migration, not an alias, so a stored value always means what the spec
 * says it means.
 */
export type FilePriority = 'routine' | 'elevated' | 'urgent';

/** Ascending order of urgency — also the coordinator queue's sort rank. */
export const FILE_PRIORITIES: FilePriority[] = ['routine', 'elevated', 'urgent'];

/** Stage 1 §F — why a file was raised above routine. */
export type PriorityReason =
  'public_safety' | 'imminent_risk' | 'multiple_complaints' | 'time_sensitive' | 'other';

export const PRIORITY_REASONS: PriorityReason[] = [
  'public_safety',
  'imminent_risk',
  'multiple_complaints',
  'time_sensitive',
  'other',
];

/**
 * Canadian postal code, `A1A 1A1`, with the space optional and case ignored.
 *
 * Deliberately not the full Canada Post rule (which also bars D, F, I, O, Q, U
 * from letter positions and W, Z from the first). This catches the errors that
 * actually happen — a mistyped or run-on code such as `E7E 1B555555` — without
 * rejecting a real record because a validator was stricter than the document it
 * was reading. A postal code is what carries a statutory notice to the owner, so
 * a malformed one means the notice does not arrive.
 */
export const POSTAL_CODE_PATTERN = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

/** Blank is allowed — the field is optional until the record supplies one. */
export function isValidPostalCode(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim();
  return trimmed === '' || POSTAL_CODE_PATTERN.test(trimmed);
}

/**
 * Whether §F requires a reason. The spec hides the field at Routine and makes
 * it mandatory at Elevated or Urgent, so this predicate governs both the form's
 * rendering and the DTO's validation — one rule, not two that can drift.
 */
export function requiresPriorityReason(priority: FilePriority): boolean {
  return priority !== 'routine';
}

/**
 * The by-law type a file is enforced under — which workflow it runs. The first
 * three run on the original D&U workflow; the rest are catalogue definitions
 * offered only where a municipality has them enabled. See `bylaw-workflows.dto`.
 */
export type BylawCategory = (typeof BYLAW_CATEGORIES)[number];

export interface FileSummary {
  id: string;
  reference: string;
  stage: FileStage;
  status: FileStatus;
  priority: FilePriority;
  emergency: boolean;
  address: string;
  // Best-effort geocode of `address`, resolved at intake; null when unavailable.
  latitude: number | null;
  longitude: number | null;
  assignedOfficerId: string | null;
  /**
   * The local government this file's enforcement belongs to — the registry row
   * the document assembler reads letterhead from. Null on files opened before
   * the anchor existed, and on any file whose local government hasn't been set;
   * those fall back to the PAN's `taxingAuthority` when a document is generated.
   */
  localGovernmentId: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Document requests from the property owner still awaiting a decision.
   *
   * Folded into the list rather than fetched separately so the count inherits
   * the list's own scoping — a caller can only ever learn this about files it
   * was already allowed to see. Absent (undefined) where a caller builds a
   * summary outside the list query; the column reads that as nothing to show,
   * which is different from a zero it has actually counted.
   */
  openDocumentRequests?: number;
  /**
   * Officer-applied labels ("vacant", "repeat complaint"), normalised by
   * `normalizeTag`. Absent on summaries built outside the list query.
   */
  tags?: string[];
}

/**
 * The per-file modules switched on at a file's municipality. Carried on the
 * detail so the file page asks a module for its data only where it can
 * answer — it used to find out by collecting a 404 from each one.
 */
export interface FileModules {
  costRecovery: boolean;
  appeals: boolean;
}

export interface FileDetail extends FileSummary {
  /**
   * Common name of the anchored local government, resolved for display so the
   * file header doesn't have to fetch the registry to render one label. Null
   * whenever `localGovernmentId` is.
   */
  localGovernmentName: string | null;
  /**
   * The Stage 1 checks answered at intake with a reason instead of a fix — a
   * PAN's PID left off the matter, a parcel outside the local government.
   * Empty on files that raised none, and on files opened before the checks.
   */
  intakeConfirmations: IntakeConfirmation[];
  /**
   * Modules on at the anchored municipality. Optional: absent on details built
   * outside `FilesService.get` (imports, fixtures), where a reader should ask
   * the module as before.
   */
  modules?: FileModules;
  complaintId: string | null;
  bylawCategory: BylawCategory;
  /**
   * Free-text companions to the coded fields whose value sets end in `other`
   * or `unknown` — the by-law it actually was, why the owner type is unclear.
   * Always null unless the sibling code is `other`/`unknown`.
   */
  bylawCategoryOther: string | null;
  /**
   * The law from the managed registry this file is enforced under, resolved for
   * display so a screen and a document need not look it up.
   *
   * Null on every file created before the registry was wired in. Readers fall
   * back to the category-derived label rather than inventing a statute.
   */
  enforcedLawId: string | null;
  enforcedLawName: string | null;
  /** Legal citation, e.g. "SNB 2017, c 19". Empty when the registry has none. */
  enforcedLawCitation: string | null;
  propertyId: string | null; // primary PID (mirror of the primary parcel)
  // Tombstone header (SNB PAN Information record, Stage 1 spec §3–§6).
  pan: string | null;
  county: string | null;
  propertyDescription: string | null;
  taxingAuthority: string | null;
  harmonizationStatus: string | null;
  ownerType: OwnerType | null;
  ownerTypeOther: string | null;
  mailingAddress: string | null;
  // Its own field, not appended to `mailingAddress` — the PAN prints it as a
  // separate column and the officer has to be able to correct it alone.
  postalCode: string | null;
  taxClass: string | null;
  neighbourhood: string | null;
  ownerSource: OwnerSource | null;
  ownerSourceOther: string | null;
  // Null on files created before the field existed.
  propertyType: PropertyType | null;
  propertyTypeOther: string | null;
  /**
   * Whether Stage 2 shows the Entry & Access Module, resolved by the API from
   * the property type's `requiresEntryAuthority` flag.
   *
   * Sent rather than derived so every client — web, mobile, and the mobile
   * workflow aggregate — branches on one answer. Each deriving it locally is
   * what let mobile keep its own copy of the old public-land list, and a copy
   * of a rule about entry authority is a copy that can go stale.
   */
  entryAccessApplies: boolean;
  // Full linked-PID list (one PAN → many PIDs). Downstream documents pull the
  // whole list, not just `propertyId`.
  parcels: ParcelDetail[];
  ownerName: string | null;
  // Owner contact for statutory correspondence; null until captured.
  ownerEmail: string | null;
  ownerLocale: 'en' | 'fr' | null;
  /** Officer-entered: the PAN record carries no telephone for anyone. */
  ownerPhone: string | null;
  ownerUnknown: boolean;
  /**
   * The occupant of the premises, where that is not the owner.
   *
   * s. 131(1) binds premises "owned **or occupied**", and s. 132 allows notice
   * to either, so a tenant is a party in their own right. Every field is
   * independently null — an officer often has a name and a phone long before an
   * address. An empty block means "no occupant recorded", never "same as the
   * owner": nothing here is inferred from the owner.
   */
  occupantName: string | null;
  occupantPhone: string | null;
  occupantAddress: string | null;
  occupantCity: string | null;
  occupantProvince: string | null;
  occupantPostalCode: string | null;
  // Stage 1 §F. Null at routine priority, where the spec hides the field.
  // Detail-only: the queue sorts on `priority`, and the reason is context the
  // coordinator reads on the file rather than a column in the list.
  priorityReason: PriorityReason | null;
  priorityReasonOther: string | null;
  priorFileId: string | null;
  notes: string | null;
  // Per-stage work captured at Stages 2–4, keyed by stage. Typed as a loose
  // record here to avoid a contracts cycle; see StageDataBag for the shape.
  stageData: Record<string, unknown> | null;
}

/**
 * How `GET /files` orders its page.
 *
 * `recent` (the default) is what every consumer other than the coordinator's
 * queue wants — a file picker or a map should show the file the officer just
 * touched, not the most urgent one in the municipality.
 *
 * `priority` is the Stage 1 §F queue ordering, and it is opt-in for a reason:
 * making it the default pushed newly created routine files off the first page
 * once enough raised-priority files existed, so the evidence picker could no
 * longer find the file the officer had just opened.
 */
export type FileListSort = 'recent' | 'priority';

export const FILE_LIST_SORTS: FileListSort[] = ['recent', 'priority'];

/**
 * The largest page `GET /files` will return.
 *
 * The dashboard, the field map and the inspection-freshness panel each need an
 * officer's whole reachable caseload, not its first twenty rows: a map that
 * silently drops the twenty-first file, or an "open files" count that stops at
 * twenty, is wrong in a way nobody can see. They ask for this much explicitly.
 * Bounded so one request cannot ask for a municipality's entire history.
 */
export const MAX_FILE_PAGE_SIZE = 1000;

export class ListFilesQueryDto {
  @IsOptional()
  @IsIn(FILE_LIST_SORTS)
  sort?: FileListSort;

  @IsOptional()
  @IsInt()
  @Min(1)
  page?: number;

  @IsOptional()
  @IsInt()
  @Min(1)
  @Max(MAX_FILE_PAGE_SIZE)
  pageSize?: number;

  @IsOptional()
  @IsString()
  stage?: FileStage;

  @IsOptional()
  @IsString()
  status?: FileStatus;
}

export class CreateFileDto {
  /** The complaint this file is opened against. Required unless `complaint` is sent. */
  @ValidateIf((o: CreateFileDto) => o.complaint === undefined)
  @IsUUID()
  complaintId?: string;

  /**
   * A complaint to record and open this file against in one request.
   *
   * Intake used to be two calls — POST /complaints, then POST /files — and a
   * file that failed to save (a bad PID, a lost connection) left the complaint
   * behind with no file, and a retry recorded it again. Sent here, the two land
   * together or not at all. Validated structurally in the service, against
   * `CreateComplaintDto`, rather than with a nested-type decorator.
   */
  @IsOptional()
  @IsObject()
  complaint?: CreateComplaintDto;

  @IsString()
  address!: string;

  /**
   * Coordinates, when the address came from an autocomplete suggestion.
   *
   * Present, they are used as-is and no geocode is attempted — a place the
   * officer picked is better located than anything a string lookup will find.
   * Absent, the server geocodes the address as it always has.
   */
  @IsOptional()
  @IsLatitude()
  latitude?: number;

  @IsOptional()
  @IsLongitude()
  longitude?: number;

  @IsOptional()
  @IsIn(BYLAW_CATEGORIES)
  bylawCategory?: BylawCategory;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  bylawCategoryOther?: string;

  /**
   * A row from the `enforced_law` registry. Validated server-side against what
   * is actually available to the file's local government — a uuid alone says
   * nothing about whether the law is active, or whether it belongs to a
   * different municipality.
   */
  @IsOptional()
  @IsUUID()
  enforcedLawId?: string;

  @IsOptional()
  @IsIn(FILE_PRIORITIES)
  priority?: FilePriority;

  // §F, two rules in one condition.
  //
  // Required once the client raises priority above routine — guarded on
  // `priority` being present so a partial update that never mentions priority
  // isn't forced to restate the reason.
  //
  // Also checked whenever a reason is supplied at all, even with no priority
  // alongside it. A reason-only PATCH carries no level for this predicate to
  // read, but the service still persists it when the *stored* priority is
  // already raised — so gating solely on the payload's priority let an
  // out-of-spec value reach the column.
  //
  // The value is validated at routine too. The service still drops it there
  // (the spec hides the field, so a valid reason arriving with a demotion is
  // stale rather than contradictory), but "about to be discarded" is no reason
  // to accept garbage.
  @ValidateIf(
    (o) =>
      o.priorityReason !== undefined ||
      (o.priority !== undefined && requiresPriorityReason(o.priority)),
  )
  @IsIn(PRIORITY_REASONS)
  priorityReason?: PriorityReason;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  priorityReasonOther?: string;

  @IsOptional()
  @IsString()
  propertyId?: string;

  // ── Tombstone header (Stage 1 spec §3–§6) ─────────────────────────────────

  @IsOptional()
  @IsString()
  @Matches(OPTIONAL_PAN_PATTERN, { message: 'pan must be a Property Account Number (digits)' })
  pan?: string;

  @IsOptional()
  @IsString()
  county?: string;

  @IsOptional()
  @IsString()
  propertyDescription?: string;

  @IsOptional()
  @IsString()
  taxingAuthority?: string;

  @IsOptional()
  @IsString()
  harmonizationStatus?: string;

  @IsOptional()
  @IsIn(OWNER_TYPES)
  ownerType?: OwnerType;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  ownerTypeOther?: string;

  // Mandatory at intake per the Stage 1 spec, but optional on the wire: files
  // are also created by the mobile workflow and by seeds, and none of those
  // paths send it yet. The officer intake form requires it; tightening this to
  // @IsIn without @IsOptional waits until every creation path is updated.
  // Validated against `gov_property_type` in the service — the accepted set is
  // administrable, so it cannot be a compile-time list.
  @IsOptional()
  @IsString()
  @MaxLength(32)
  propertyType?: PropertyType;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  propertyTypeOther?: string;

  @IsOptional()
  @IsString()
  mailingAddress?: string;

  // `@IsOptional` skips only null/undefined, so a cleared field — which arrives
  // as '' or as the whitespace left behind by selecting and deleting — would
  // otherwise be measured against the pattern and rejected. Blank is a
  // legitimate value here: the record may simply not carry a postal code.
  @IsOptional()
  @IsString()
  @MaxLength(16)
  @ValidateIf((o: { postalCode?: string }) => (o.postalCode ?? '').trim() !== '')
  @Matches(POSTAL_CODE_PATTERN, { message: 'postalCode must look like A1A 1A1' })
  postalCode?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  taxClass?: string;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  neighbourhood?: string;

  @IsOptional()
  @IsIn(['snb_land_registry', 'manual', 'other'])
  ownerSource?: OwnerSource;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  ownerSourceOther?: string;

  // Additional PIDs (one PAN → many PIDs). Validated structurally in the service.
  @IsOptional()
  @IsArray()
  parcels?: ParcelInputDto[];

  @IsOptional()
  @IsString()
  ownerName?: string;

  @IsOptional()
  @IsEmail()
  ownerEmail?: string;

  @IsOptional()
  @IsIn(['en', 'fr'])
  ownerLocale?: 'en' | 'fr';

  /** Officer-entered: the PAN record carries no telephone for anyone. */
  @IsOptional()
  @IsString()
  @MaxLength(32)
  ownerPhone?: string;

  @IsOptional()
  @IsBoolean()
  ownerUnknown?: boolean;

  /**
   * The occupant of the premises (LGA s. 131(1), s. 132) — a party to the
   * enforcement in their own right, never inferred from the owner.
   */
  @IsOptional()
  @IsString()
  @MaxLength(200)
  occupantName?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  occupantPhone?: string;

  @IsOptional()
  @IsString()
  @MaxLength(400)
  occupantAddress?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  occupantCity?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  occupantProvince?: string;

  @IsOptional()
  @IsString()
  @MaxLength(16)
  occupantPostalCode?: string;

  /**
   * Staged SNB extractions to attach to the new file.
   *
   * An officer can upload and extract from the intake form before a file exists.
   * The uploaded PDFs sit under `staging/` until this call claims them, at which
   * point they are re-keyed to `documents/{fileId}/…` and the extraction rows are
   * repointed at the file. Unclaimed staged objects are swept after 24 hours.
   */
  @IsOptional()
  @IsArray()
  @IsUUID(undefined, { each: true })
  extractionStagingIds?: string[];

  /**
   * Answers to the Stage 1 checks the staged records raised: a PID the PAN
   * lists with no Parcel record, confirmed not part of this matter, and a
   * parcel whose record places it outside the file's local government,
   * confirmed as belonging here. Each needs a reason. The API recomputes the
   * checks from the staged records and refuses a file that leaves one
   * unanswered. Validated structurally in the service.
   */
  @IsOptional()
  @IsArray()
  intakeConfirmations?: IntakeConfirmationInput[];

  /**
   * The local government whose enforcement this is.
   *
   * Required since the scoped access model: the anchor *is* the tenant boundary,
   * and a file without one sits outside every municipality — reachable by nobody
   * but a Munifocus operator, and invisible to the officer who opened it. It was
   * optional while every account could see every file, which is exactly the
   * problem the anchor now solves.
   *
   * Intake fills it in without asking whenever the officer holds a single
   * appointment; an officer appointed at several municipalities is asked, and
   * should be — which town a file belongs to is not a detail to guess at.
   * Validated against the registry.
   */
  @IsUUID()
  localGovernmentId!: string;

  /**
   * Route the file to an officer at intake instead of leaving it on the queue.
   *
   * Only honoured alongside `localGovernmentId`: assignment authority is checked
   * against the registry, and an unanchored file has no registry row to check
   * against. Rejected when the operator holds no active officer appointment
   * there — the same gate a later re-assignment answers to.
   */
  @IsOptional()
  @IsUUID()
  assignedOfficerId?: string;

  @IsOptional()
  @IsString()
  notes?: string;
}

export class UpdateFileDto {
  @IsOptional()
  @IsString()
  address?: string;

  @IsOptional()
  @IsString()
  notes?: string;

  @IsOptional()
  @IsIn(BYLAW_CATEGORIES)
  bylawCategory?: BylawCategory;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  bylawCategoryOther?: string;

  /**
   * A row from the `enforced_law` registry. Validated server-side against what
   * is actually available to the file's local government — a uuid alone says
   * nothing about whether the law is active, or whether it belongs to a
   * different municipality.
   */
  @IsOptional()
  @IsUUID()
  enforcedLawId?: string;

  @IsOptional()
  @IsIn(FILE_PRIORITIES)
  priority?: FilePriority;

  // §F, two rules in one condition.
  //
  // Required once the client raises priority above routine — guarded on
  // `priority` being present so a partial update that never mentions priority
  // isn't forced to restate the reason.
  //
  // Also checked whenever a reason is supplied at all, even with no priority
  // alongside it. A reason-only PATCH carries no level for this predicate to
  // read, but the service still persists it when the *stored* priority is
  // already raised — so gating solely on the payload's priority let an
  // out-of-spec value reach the column.
  //
  // The value is validated at routine too. The service still drops it there
  // (the spec hides the field, so a valid reason arriving with a demotion is
  // stale rather than contradictory), but "about to be discarded" is no reason
  // to accept garbage.
  @ValidateIf(
    (o) =>
      o.priorityReason !== undefined ||
      (o.priority !== undefined && requiresPriorityReason(o.priority)),
  )
  @IsIn(PRIORITY_REASONS)
  priorityReason?: PriorityReason;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  priorityReasonOther?: string;

  @IsOptional()
  @IsString()
  propertyId?: string;

  // ── Tombstone header (Stage 1 spec §3–§6) ─────────────────────────────────

  @IsOptional()
  @IsString()
  @Matches(OPTIONAL_PAN_PATTERN, { message: 'pan must be a Property Account Number (digits)' })
  pan?: string;

  @IsOptional()
  @IsString()
  county?: string;

  @IsOptional()
  @IsString()
  propertyDescription?: string;

  @IsOptional()
  @IsString()
  taxingAuthority?: string;

  @IsOptional()
  @IsString()
  harmonizationStatus?: string;

  @IsOptional()
  @IsIn(OWNER_TYPES)
  ownerType?: OwnerType;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  ownerTypeOther?: string;

  // Editable after intake: property type can be corrected once the parcel is
  // confirmed at Stage 2, and changing it changes whether the Entry & Access
  // Module renders.
  // Validated against `gov_property_type` in the service — the accepted set is
  // administrable, so it cannot be a compile-time list.
  @IsOptional()
  @IsString()
  @MaxLength(32)
  propertyType?: PropertyType;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  propertyTypeOther?: string;

  @IsOptional()
  @IsString()
  mailingAddress?: string;

  // `@IsOptional` skips only null/undefined, so a cleared field — which arrives
  // as '' or as the whitespace left behind by selecting and deleting — would
  // otherwise be measured against the pattern and rejected. Blank is a
  // legitimate value here: the record may simply not carry a postal code.
  @IsOptional()
  @IsString()
  @MaxLength(16)
  @ValidateIf((o: { postalCode?: string }) => (o.postalCode ?? '').trim() !== '')
  @Matches(POSTAL_CODE_PATTERN, { message: 'postalCode must look like A1A 1A1' })
  postalCode?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  taxClass?: string;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  neighbourhood?: string;

  @IsOptional()
  @IsIn(['snb_land_registry', 'manual', 'other'])
  ownerSource?: OwnerSource;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  ownerSourceOther?: string;

  // Full replace-on-save of the linked-PID list (reconciled by id in the service).
  @IsOptional()
  @IsArray()
  parcels?: ParcelInputDto[];

  @IsOptional()
  @IsString()
  ownerName?: string;

  @IsOptional()
  @IsEmail()
  ownerEmail?: string;

  @IsOptional()
  @IsIn(['en', 'fr'])
  ownerLocale?: 'en' | 'fr';

  /** Officer-entered: the PAN record carries no telephone for anyone. */
  @IsOptional()
  @IsString()
  @MaxLength(32)
  ownerPhone?: string;

  @IsOptional()
  @IsBoolean()
  ownerUnknown?: boolean;

  /**
   * The occupant of the premises (LGA s. 131(1), s. 132) — a party to the
   * enforcement in their own right, never inferred from the owner.
   */
  @IsOptional()
  @IsString()
  @MaxLength(200)
  occupantName?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  occupantPhone?: string;

  @IsOptional()
  @IsString()
  @MaxLength(400)
  occupantAddress?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  occupantCity?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  occupantProvince?: string;

  @IsOptional()
  @IsString()
  @MaxLength(16)
  occupantPostalCode?: string;

  @IsOptional()
  @IsBoolean()
  emergency?: boolean;

  /**
   * Re-anchor the file to a different local government. Rare — an anchor is
   * normally set once — but a file opened against the wrong municipality has to
   * be correctable, and the correction is what fixes every document generated
   * from then on. Validated against the registry.
   *
   * Re-anchoring clears the assignment when the current officer holds no active
   * appointment at the new local government: their authority does not follow the
   * file across a boundary.
   *
   * Explicit `null` un-anchors — the correction for a file opened against a
   * municipality when the property is in fact in a local service district, which
   * has no registry row to point at. Distinct from omitting the key, which
   * leaves the current anchor alone. (`@IsOptional` waves null through; the
   * service reads `!== undefined`, so the two stay distinguishable.)
   */
  @IsOptional()
  @IsUUID()
  localGovernmentId?: string | null;

  /** Explicit `null` clears the assignment and returns the file to the queue. */
  @IsOptional()
  @IsUUID()
  assignedOfficerId?: string | null;
}
